/**
 * Onboarding Steps Constants (UA-5.2)
 *
 * Defines the available onboarding steps and their default requirement levels
 * per invite type. Used by the admin onboarding step configurator.
 */

import type { InviteType } from "@/types/InviteType";
import type {
  OnboardingStepDefinition,
  OnboardingStepRequirement,
} from "@/types/OnboardingConfig";

// ---------------------------------------------------------------------------
// Step definitions
// ---------------------------------------------------------------------------

export const ONBOARDING_STEP_DEFINITIONS: OnboardingStepDefinition[] = [
  {
    step_id: "complete_profile",
    label: "Complete your profile",
    description: "Name, avatar, basic info",
  },
  {
    step_id: "connect_corporate",
    label: "Connect your corporate account",
    description: "Link to an organization",
  },
  {
    step_id: "upload_spend_file",
    label: "Upload first AP spend file",
    description: "Import AP data",
  },
  {
    // ID aligned to config/onboardingSteps.ts (source of truth for completion tracking)
    step_id: "configure_card_terms",
    label: "Configure card program terms",
    description: "Set up rebate tiers",
  },
  {
    // ID aligned to config/onboardingSteps.ts (source of truth for completion tracking)
    step_id: "set_financial_profile",
    label: "Set treasury financial profile",
    description: "WACC, DPO, tax rate",
  },
  {
    step_id: "create_campaign",
    label: "Create first campaign",
    description: "Launch a supplier campaign",
  },
  {
    // ID aligned to config/onboardingSteps.ts (source of truth for completion tracking)
    step_id: "launch_drip",
    label: "Launch first drip sequence",
    description: "Set up email outreach",
  },
];

// ---------------------------------------------------------------------------
// Invite types with human-readable labels
// ---------------------------------------------------------------------------

export interface InviteTypeDefinition {
  value: InviteType;
  label: string;
}

export const INVITE_TYPE_DEFINITIONS: InviteTypeDefinition[] = [
  { value: "platform_user", label: "Platform User" },
  { value: "buyer_standard", label: "Buyer (Standard)" },
  { value: "issuer_invited_buyer", label: "Issuer-Invited Buyer" },
  { value: "supplier_enrollment", label: "Supplier Enrollment" },
  { value: "issuer_staff", label: "Issuer Staff" },
];

// ---------------------------------------------------------------------------
// Default requirements per (step_id, invite_type)
// ---------------------------------------------------------------------------

/**
 * Mapping: step_id -> invite_type -> default requirement.
 * Steps not listed for a given invite type default to "hidden".
 */
export const DEFAULT_STEP_REQUIREMENTS: Record<
  string,
  Partial<Record<InviteType, OnboardingStepRequirement>>
> = {
  complete_profile: {
    platform_user: "required",
    buyer_standard: "required",
    issuer_invited_buyer: "required",
    supplier_enrollment: "required",
    issuer_staff: "required",
  },
  connect_corporate: {
    buyer_standard: "required",
    issuer_staff: "required",
  },
  upload_spend_file: {
    buyer_standard: "required",
  },
  // IDs aligned to config/onboardingSteps.ts (source of truth for completion tracking)
  configure_card_terms: {
    buyer_standard: "required",
    issuer_invited_buyer: "required",
  },
  set_financial_profile: {
    buyer_standard: "required",
    issuer_invited_buyer: "required",
  },
  create_campaign: {
    buyer_standard: "required",
  },
  launch_drip: {
    buyer_standard: "required",
  },
};

/**
 * Resolve the default requirement for a given step + invite type.
 * Falls back to "hidden" when no explicit default is configured.
 */
export function getDefaultRequirement(
  stepId: string,
  inviteType: InviteType,
): OnboardingStepRequirement {
  return DEFAULT_STEP_REQUIREMENTS[stepId]?.[inviteType] ?? "hidden";
}
